/**
 * Comparing two runs.
 *
 * The core educational moment, and the reason the whole engine is deterministic: pin the
 * seed, change one thing, and the difference between the two curves is attributable to
 * that one thing rather than to luck. It is the difference between "movements sometimes
 * win" and "this movement would have won with a strike fund".
 *
 * The baseline is whatever the run was when you pressed compare; the variant is whatever
 * you have edited since. Both are already in memory, so this costs nothing to draw.
 */

import { useMemo } from 'react';
import type { RunLog } from 'walkout-engine';
import './Compare.css';

const OUTCOME_LABEL: Record<RunLog['outcome']['kind'], string> = {
  conceded: 'Demands met',
  negotiated: 'Partial settlement',
  stalemate: 'Neither side broke',
  collapse: 'Collapsed',
  'violent-escalation': 'Turned violent',
};

function pct(value: number): string {
  const percent = value * 100;
  if (percent >= 10) return `${Math.round(percent)}%`;
  return `${percent.toFixed(1)}%`;
}

function delta(a: number, b: number, format: (v: number) => string): string {
  const difference = b - a;
  if (Math.abs(difference) < 1e-9) return 'no change';
  return `${difference > 0 ? '+' : '−'}${format(Math.abs(difference))}`;
}

/** Two participation curves on one set of axes. */
function Curves({ baseline, variant }: { baseline: RunLog; variant: RunLog }) {
  const width = 560;
  const height = 190;
  const lastDay = Math.max(baseline.days.length, variant.days.length) - 1;
  const peak = Math.max(
    0.05,
    baseline.peakParticipation.value,
    variant.peakParticipation.value,
  );

  const path = (run: RunLog): string =>
    run.days
      .map((d, i) => {
        const x = lastDay === 0 ? 0 : (d.day / lastDay) * width;
        const y = height - (d.participation / peak) * height;
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');

  return (
    <svg className="compare__chart" viewBox={`0 0 ${width} ${height + 22}`} role="img"
      aria-label={`Participation over time. Baseline peaked at ${pct(baseline.peakParticipation.value)}, variant at ${pct(variant.peakParticipation.value)}.`}>
      <rect x="0" y="0" width={width} height={height} fill="var(--paper)" stroke="var(--ink-black)" strokeWidth="2" />
      <path d={path(baseline)} className="compare__line compare__line--baseline" />
      <path d={path(variant)} className="compare__line compare__line--variant" />
      <text x="0" y={height + 16} className="compare__axis">day 0</text>
      <text x={width} y={height + 16} className="compare__axis" textAnchor="end">
        day {lastDay}
      </text>
      <text x={width / 2} y={height + 16} className="compare__axis" textAnchor="middle">
        peak {pct(peak)}
      </text>
    </svg>
  );
}

export function Compare({
  baseline,
  variant,
  changes,
  onClose,
}: {
  baseline: RunLog;
  variant: RunLog;
  changes: string[];
  onClose: () => void;
}) {
  const rows = useMemo(
    () => [
      {
        label: 'Outcome',
        a: OUTCOME_LABEL[baseline.outcome.kind],
        b: OUTCOME_LABEL[variant.outcome.kind],
        changed: baseline.outcome.kind !== variant.outcome.kind,
      },
      {
        label: 'Ended on day',
        a: String(baseline.outcome.day),
        b: String(variant.outcome.day),
        changed: baseline.outcome.day !== variant.outcome.day,
        note: delta(baseline.outcome.day, variant.outcome.day, (v) => `${Math.round(v)} days`),
      },
      {
        label: 'Peak participation',
        a: pct(baseline.peakParticipation.value),
        b: pct(variant.peakParticipation.value),
        changed:
          Math.abs(baseline.peakParticipation.value - variant.peakParticipation.value) > 0.002,
        note: delta(baseline.peakParticipation.value, variant.peakParticipation.value, pct),
      },
      {
        label: 'Widest support gap',
        a: pct(baseline.widestGap.value),
        b: pct(variant.widestGap.value),
        changed: Math.abs(baseline.widestGap.value - variant.widestGap.value) > 0.002,
        note: delta(baseline.widestGap.value, variant.widestGap.value, pct),
      },
      {
        label: 'Institutions lost',
        a: `${baseline.days.at(-1)?.pillars.filter((p) => p.defectedOn !== null).length ?? 0}`,
        b: `${variant.days.at(-1)?.pillars.filter((p) => p.defectedOn !== null).length ?? 0}`,
        changed:
          (baseline.days.at(-1)?.pillars.filter((p) => p.defectedOn !== null).length ?? 0) !==
          (variant.days.at(-1)?.pillars.filter((p) => p.defectedOn !== null).length ?? 0),
      },
    ],
    [baseline, variant],
  );

  const sameSeed = baseline.config.seed === variant.config.seed;

  return (
    <section className="compare">
      <header className="compare__head">
        <h2>Two runs</h2>
        <button type="button" onClick={onClose}>
          close
        </button>
      </header>

      <p className={`compare__seed${sameSeed ? '' : ' compare__seed--warn'}`}>
        {sameSeed
          ? `Same seed (${baseline.config.seed}). Every difference below is caused by what you changed, not by luck.`
          : `Different seeds (${baseline.config.seed} and ${variant.config.seed}). Some of this difference is luck — set them equal to isolate the change.`}
      </p>

      <div className="compare__changes">
        <span className="label">What changed</span>
        {changes.length === 0 ? (
          <p className="compare__nochange">
            Nothing yet. Edit a parameter and this fills in.
          </p>
        ) : (
          <ul>
            {changes.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
        )}
      </div>

      <Curves baseline={baseline} variant={variant} />

      <table className="compare__table">
        <thead>
          <tr>
            <th />
            <th>Baseline</th>
            <th>Now</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className={row.changed ? 'compare__row--changed' : ''}>
              <th scope="row">{row.label}</th>
              <td>{row.a}</td>
              <td>
                {row.b}
                {row.changed && row.note && <span className="compare__delta">{row.note}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
